import React, { useState } from "react";
import { PageHero, CTAStripe } from "@/components/sections/Shared";
import QuoteDialog from "@/components/QuoteDialog";
import { BUSINESS, IMAGES } from "@/lib/business";
import { ShieldCheck, Users, Award, Heart } from "lucide-react";
import SEO from "@/components/SEO";

const VALUES = [
    {
        icon: ShieldCheck,
        title: "Security first",
        desc: "Every product we recommend is one we'd install in our own homes.",
    },
    {
        icon: Heart,
        title: "Customer-focused",
        desc: "Honest advice, fair pricing, and real follow-through after install.",
    },
    {
        icon: Award,
        title: "Quality craftsmanship",
        desc: "Certified installers. Clean work. Finishes that last for years.",
    },
    {
        icon: Users,
        title: "Locally committed",
        desc: "Canadian-owned and proud to serve the GTA community.",
    },
];

export default function About() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="About GTA Secure Inc. — Canadian-Owned Security Experts"
                description="Headquartered in Georgetown, Ontario, GTA Secure Inc. is a Canadian-owned team specialising in safety window films, security cameras, and custom blinds & shutters across the GTA."
                path="/about"
                keywords="GTA Secure about, Canadian-owned security company, Georgetown Ontario, window film experts, security camera installers"
            />
            <PageHero
                eyebrow="About Us"
                title="A Canadian-owned team, dedicated to your peace of mind."
                subtitle={`Based out of ${BUSINESS.address.line2}, we've been helping Greater Toronto Area homeowners and businesses protect, style, and enjoy their spaces with premium window films, smart cameras, and custom blinds.`}
            />

            <section className="section bg-white">
                <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Our Story
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Three services. One standard — yours.
                        </h2>
                        <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
                            <p>
                                GTA Secure Inc. was founded to bring real, premium-grade security
                                and window solutions to everyday homes and businesses — without the
                                confusing jargon or high-pressure sales tactics.
                            </p>
                            <p>
                                We specialise in three things and we do them with care:
                                residential and commercial safety window films, professionally
                                installed security camera systems, and custom-made blinds &amp;
                                shutters. Because we're Canadian-owned and local, you'll speak to
                                the same people from quote to installation — and beyond.
                            </p>
                            <p>
                                Whether you're hardening a storefront, outfitting a new home, or
                                simply replacing tired blinds, we treat your space like it's ours.
                            </p>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="aspect-[5/6] overflow-hidden rounded-[28px] bg-navy/5">
                            <img
                                src={IMAGES.gallery[4]}
                                alt="About GTA Secure"
                                className="h-full w-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> What we stand for
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            The values that guide every install.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {VALUES.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <div
                                    key={v.title}
                                    data-testid={`value-${i}`}
                                    className="card-premium p-7"
                                >
                                    <div className="h-12 w-12 rounded-xl bg-white border border-gold/30 text-gold flex items-center justify-center mb-5">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-xl font-semibold text-navy">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                                        {v.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <CTAStripe
                heading="Partner with a local team who cares."
                subheading="Tell us about your project — we'll handle the rest."
                ctaLabel="Get My Free Quote"
                onCta={() => setQuoteOpen(true)}
            />
            <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
        </>
    );
}
